import * as React from 'react';

import type { W_FieldReportAssignmentHistory_FindForFieldReport_VM } from 'ab-api/web/fieldReportAssignmentHistory/findForFieldReport/viewModel';

import { formatDate, getOffset, offsetTime } from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import type TimeZoneSourceType from 'ab-enums/timezoneSourceType.enum';

import { Button } from '@acceligentllc/storybook';

import CustomModal from 'af-components/CustomModal';
import TimezoneSourceBanner from 'af-components/TimezoneSourceBanner';

import { FieldReportHistoryRowFactory } from './helpers';
import Loading from './Loading';
import styles from './styles.module.scss';

interface OwnProps {
	woCode: string;
	assigmentHistoryRows: W_FieldReportAssignmentHistory_FindForFieldReport_VM[];
	showModal: boolean;
	isLoading: boolean;
	timeZoneInUse: Nullable<string>;
	timezoneSourceType: Nullable<TimeZoneSourceType>;
	closeModal: () => void;
}

type Props = OwnProps;

const Modal: React.FC<Props> = (props: Props) => {

	const {
		woCode,
		assigmentHistoryRows,
		showModal,
		isLoading,
		closeModal,
		timeZoneInUse,
		timezoneSourceType,
	} = props;

	const browserOffset = React.useMemo(() => (new Date()).getTimezoneOffset(), []);
	const timeZoneOffset = React.useMemo(() => timeZoneInUse ? getOffset(timeZoneInUse) : 0, [timeZoneInUse]);

	const formatTimestamp = React.useCallback((timestamp: string) => {
		return timeZoneOffset ? formatDate(offsetTime(timestamp, timeZoneOffset + browserOffset, 'minutes', TimeFormat.ISO_DATETIME), TimeFormat.FULL_DATE, TimeFormat.ISO_DATETIME)
			: formatDate(timestamp, TimeFormat.FULL_DATE, TimeFormat.ISO_DATETIME);
	}, [browserOffset, timeZoneOffset]);

	return (
		<CustomModal
			closeModal={closeModal}
			modalStyle="info"
			showModal={showModal}
			size="lg"
		>
			<CustomModal.Header
				closeModal={closeModal}
				title={woCode}
			/>
			<TimezoneSourceBanner
				showLocalTimeZone={true}
				timezoneInUse={timeZoneInUse}
				timezoneSourceType={timezoneSourceType}
			/>
			<CustomModal.Body
				padding="none"
			>
				{isLoading ?
					<Loading />
					:
					<div className={styles['assignment-history-table-container']}>
						<table className={styles['assignment-history-table']}>
							<thead>
								<tr className={styles['assignment-history-header']}>
									<th>Laborer</th>
									<th>Reason</th>
									<th>Timestamp</th>
								</tr>
							</thead>
							<tbody>
								{assigmentHistoryRows.map(renderRow)}
							</tbody>
						</table>

					</div>
				}
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Close"
					onClick={closeModal}
					style="secondary"
				/>
			</CustomModal.Footer>
		</CustomModal >
	);

	function renderRow(historyRowRecord: W_FieldReportAssignmentHistory_FindForFieldReport_VM, index: number) {

		const fieldReportHistoryRow = FieldReportHistoryRowFactory.make(historyRowRecord);

		return (
			<tr className={styles['assignment-history-row']} key={index}>
				<td>{fieldReportHistoryRow.getAssignee()}</td>
				<td className={styles['assignment-history-row__reason']}>{fieldReportHistoryRow.getReason()}</td>
				<td>{formatTimestamp(fieldReportHistoryRow.getISOstring())}</td>
			</tr>
		);
	}
};

export default React.memo(Modal);
